/* ---------------------------------------------------------------------------
 * Unified store search — v3 (2026-09-06). DEPLOYED on the draft theme 142335672395.
 *
 * Shopify answers for gear and apparel; the parts endpoint (/apps/parts/search, ~31,600 Triumph
 * parts and accessories that native search cannot see by design) answers for the rest. One box,
 * one results panel.
 *
 * WHAT v2 GOT WRONG, measured 2026-09-05 (reports/main-draft.md B2): it inserted its own results
 * host after the <form> inside Dawn's vertically-centred modal flex box, so a 500px block was
 * centred on an 88px row — top clipped at y=-129, the rest painted over the header with no
 * background. It also mounted on Dawn's hidden `options[prefix]` input, and had no page mode, so
 * /search?q=T2701520 said "No results found" for a $133.90 part the endpoint knows.
 *
 * v3 RULES
 *  1. One mount per `input[name="q"]`, nothing else.
 *  2. Parts render INSIDE Dawn's own `#predictive-search-results`, after Shopify's groups and
 *     before the "Search for" button, as ordinary `li[role=option]` items — so Dawn's panel
 *     positioning, background, z-index, scrolling and arrow/Enter/Escape handling all apply
 *     unchanged. This file owns no keyboard code.
 *  3. The "no online match" copy never sits above a real Shopify hit: parts append below; when
 *     parts are empty and Shopify has hits, nothing is added; when BOTH are empty the panel shows
 *     the recovery block (categories, parts catalogue, parts desk).
 *  4. /search page mode: `#scm-parts-results` (rendered by sections/main-search.liquid) is filled
 *     with the endpoint's hits below Shopify's results; with zero Shopify results the status line
 *     becomes "No gear or apparel matched X. Parts results are below." (PARTS_LAUNCH_IA §4).
 *
 * FITMENT IS NEVER CLAIMED HERE. Results report SUPPLY. Only the part page, with a motorcycle
 * chosen, may say whether something fits.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var ENDPOINT = '/apps/parts/search';
  var PARTS_HOME = '/apps/parts/';
  var DESK_TEL = 'tel:+17142566700', DESK_LABEL = '(714) 256-6700';
  var MIN = 2, DEBOUNCE = 200, PANEL_LIMIT = 5, PAGE_LIMIT = 12;
  var CATS = [
    ['Helmets', '/collections/helmets'],
    ['Riding Gear', '/collections/riding-gear'],
    ['Casual & Lifestyle', '/collections/apparel'],
    ['Sale', '/collections/sale']
  ];
  var LABEL_CLASS = {
    'In Stock': 'scm-ps__av--stock', 'Available': 'scm-ps__av--avail',
    'Limited Availability': 'scm-ps__av--low', 'Out of Stock': 'scm-ps__av--out'
  };

  function esc(x) {
    return String(x == null ? '' : x).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function money(v) {
    if (v == null || v === '') return '';
    return '$' + Number(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function catalogueUrl(q) { return PARTS_HOME + '?q=' + encodeURIComponent(q); }

  var inflight = null;
  function query(q, limit) {
    if (inflight && inflight.abort) inflight.abort();
    var ctl = window.AbortController ? new AbortController() : null;
    inflight = ctl;
    return fetch(ENDPOINT + '?q=' + encodeURIComponent(q) + '&limit=' + limit,
      { credentials: 'same-origin', signal: ctl ? ctl.signal : undefined })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });   /* search must never throw into the page */
  }

  function metaHtml(r) {
    var av = r.availability
      ? '<span class="scm-ps__av ' + (LABEL_CLASS[r.availability] || '') + '">' + esc(r.availability) + '</span>' : '';
    return '<span class="scm-ps__meta">'
      + (r.partNumber ? '<span class="scm-ps__pn">' + esc(r.partNumber) + '</span>' : '')
      + (r.price ? '<span class="scm-ps__price">' + esc(money(r.price)) + '</span>' : '')
      + av + '</span>';
  }

  /* ---- predictive panel markup: Dawn's own classes so it reads as one panel ---- */
  var optSeq = 0;
  function panelGroupsHtml(res, q, perGroup) {
    var html = '<div class="scm-ps" data-scm-ps>';
    if (res.corrected) {
      html += '<p class="scm-ps__corrected" role="status">Showing parts for <strong>' + esc(res.corrected) + '</strong></p>';
    }
    res.groups.forEach(function (g) {
      /* The TYPE is the label. A customer must know whether they are opening an accessory, an
         individual OEM part or a diagram before they click. */
      var hid = 'scm-ps-head-' + (++optSeq);
      html += '<h2 id="' + hid + '" class="predictive-search__heading text-body caption-with-letter-spacing">' + esc(g.type) + '</h2>'
        + '<ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="' + hid + '">';
      g.results.slice(0, perGroup).forEach(function (r) {
        html += '<li id="scm-ps-opt-' + (++optSeq) + '" class="predictive-search__list-item scm-ps__item" role="option" aria-selected="false">'
          + '<a href="' + esc(r.url) + '" class="predictive-search__item link link--text" tabindex="-1">'
          + '<div class="predictive-search__item-content">'
          + '<p class="predictive-search__item-heading h5">' + esc(r.title) + '</p>' + metaHtml(r)
          + '</div></a></li>';
      });
      html += '</ul>';
    });
    html += '<div class="scm-ps__all"><a class="link link--text" href="' + esc(catalogueUrl(q)) + '">'
      + 'All parts results for &ldquo;' + esc(q) + '&rdquo; &rarr;</a></div></div>';
    return html;
  }

  function recoveryHtml(q) {
    var html = '<div class="scm-ps scm-ps--empty" data-scm-ps>'
      + '<p class="scm-ps__msg" role="status">Nothing matched &ldquo;' + esc(q) + '&rdquo; in gear, apparel or Triumph parts. Try another word, or start here:</p>'
      + '<ul class="predictive-search__results-list list-unstyled scm-ps__cats" role="group" aria-label="Top categories">';
    CATS.forEach(function (c) {
      html += '<li id="scm-ps-opt-' + (++optSeq) + '" class="predictive-search__list-item scm-ps__cat" role="option" aria-selected="false">'
        + '<a href="' + c[1] + '" class="predictive-search__item link link--text" tabindex="-1">' + esc(c[0]) + '</a></li>';
    });
    html += '</ul>'
      + '<p class="scm-ps__note">Looking for a Triumph part number? <a class="link" href="' + esc(catalogueUrl(q)) + '">Search the parts catalogue &rarr;</a></p>'
      + '<p class="scm-ps__note">Parts desk: <a class="link" href="' + DESK_TEL + '">' + DESK_LABEL + '</a></p>'
      + '</div>';
    return html;
  }

  /* Put our block into Dawn's rendered results. Idempotent: the previous block is replaced. */
  function inject(panel, q, res) {
    var host = panel.querySelector('#predictive-search-results');
    if (!host) return;
    var old = host.querySelector('[data-scm-ps]');
    if (old) old.remove();
    if (!res) return;
    var shopHits = !!host.querySelector('#predictive-search-results-groups-wrapper li[role="option"]');
    /* With Shopify hits above, keep the parts group short so it starts inside the fold. */
    var html = res.total ? panelGroupsHtml(res, q, shopHits ? 3 : PANEL_LIMIT) : (shopHits ? '' : recoveryHtml(q));
    if (!html) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    host.insertBefore(tmp.firstElementChild, host.querySelector('.predictive-search__search-for-button'));
  }

  function mount(input) {
    if (!input || input.__scmSearch) return;
    input.__scmSearch = true;
    var ps = input.closest('predictive-search');
    var panel = ps ? ps.querySelector('[data-predictive-search]') : null;
    if (!panel) return;                       /* predictive search disabled: page mode only */

    var state = { q: '', res: null }, t = null;
    function sync() {
      var q = input.value.trim();
      if (state.q === q) inject(panel, q, state.res);
    }
    /* Dawn replaces the panel's children on every render (including cache hits); re-add ours.
       childList without subtree: our own insertion, two levels down, does not re-trigger it. */
    new MutationObserver(function () {
      if (!panel.querySelector('[data-scm-ps]')) sync();
    }).observe(panel, { childList: true });

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (t) clearTimeout(t);
      if (q !== state.q) {
        var stale = panel.querySelector('[data-scm-ps]');
        if (stale) stale.remove();
      }
      if (q.length < MIN) { state = { q: q, res: null }; return; }
      t = setTimeout(function () {
        query(q, PANEL_LIMIT).then(function (res) {
          if (input.value.trim() !== q) return;
          state = { q: q, res: res };
          sync();
        });
      }, DEBOUNCE);
    });
  }

  /* ---- /search page mode ---- */
  function pageCardHtml(r) {
    return '<li class="scm-sr__card"><a class="scm-sr__link" href="' + esc(r.url) + '">'
      + '<span class="scm-sr__title">' + esc(r.title) + '</span>'
      + (r.partNumber ? '<span class="scm-sr__pn">' + esc(r.partNumber) + '</span>' : '')
      + '<span class="scm-sr__row">'
      + (r.price ? '<span class="scm-sr__price">' + esc(money(r.price)) + '</span>' : '')
      + (r.availability ? '<span class="scm-ps__av ' + (LABEL_CLASS[r.availability] || '') + '">' + esc(r.availability) + '</span>' : '')
      + '</span></a></li>';
  }
  function pageMode() {
    var box = document.getElementById('scm-parts-results');
    if (!box) return;
    var q = (box.getAttribute('data-q') || '').trim();
    if (q.length < MIN) return;
    var shopHits = parseInt(box.getAttribute('data-results') || '0', 10) > 0;
    query(q, PAGE_LIMIT).then(function (res) {
      if (!res || !res.total) return;         /* Liquid already rendered the recovery block */
      var html = '<h2 class="scm-sr__head h2">Triumph parts &amp; accessories</h2>'
        + '<p class="scm-sr__sub">' + res.total + (res.total === 1 ? ' match' : ' matches')
        + (res.corrected ? ' for &ldquo;' + esc(res.corrected) + '&rdquo;' : '')
        + '. Availability is shown per part; fitment is confirmed on the part page.</p>';
      res.groups.forEach(function (g) {
        html += '<h3 class="scm-sr__subhead">' + esc(g.type) + '</h3><ul class="scm-sr__grid list-unstyled" role="list">'
          + g.results.map(pageCardHtml).join('') + '</ul>';
      });
      html += '<p class="scm-sr__more"><a class="button button--secondary" href="' + esc(catalogueUrl(q)) + '">'
        + 'All parts results for &ldquo;' + esc(q) + '&rdquo;</a></p>';
      box.innerHTML = html;
      box.hidden = false;
      if (!shopHits) {
        var status = document.getElementById('scm-search-status');
        if (status) status.textContent = 'No gear or apparel matched “' + q + '”. Parts results are below.';
        var rec = document.getElementById('scm-search-recovery');
        if (rec) rec.hidden = true;
      }
    });
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('input[name="q"]'), mount);
    pageMode();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
